export type Market = {
  loanToken: string // 20 bytes (EVM address, hex)
  collateralToken: string // 20 bytes (EVM address, hex)
  oracle: string // 20 bytes (EVM address, hex)
  irm: string // 20 bytes (EVM address, hex)
  lltv: bigint // 16 bytes
  price: bigint // 32 bytes (for morpho)
  loanTokenPrice?: bigint // 32 bytes (for moolah)
  collateralTokenPrice?: bigint // 32 bytes (for moolah)
  rateAtTarget: bigint // 32 bytes
  totalSupplyAssets: bigint // 16 bytes
  totalSupplyShares: bigint // 16 bytes
  totalBorrowAssets: bigint // 16 bytes
  totalBorrowShares: bigint // 16 bytes
  lastUpdate: bigint // 16 bytes
  fee: bigint // 16 bytes
}

export type ListaMarket = {
  loanToken: string
  collateralToken: string
  oracle: string
  irm: string
  lltv: bigint

  price: bigint
  loanTokenPrice?: bigint
  collateralTokenPrice?: bigint

  minLoan?: bigint
  rateCap?: bigint
  rateFloor?: bigint
  hasWhitelist?: boolean
  loanProvider?: string
  collateralProvider?: string
  broker?: string

  rateAtTarget: bigint
  totalSupplyAssets: bigint
  totalSupplyShares: bigint
  totalBorrowAssets: bigint
  totalBorrowShares: bigint
  lastUpdate: bigint
  fee: bigint
}

const FIELD_SIZES = {
  addr: 20,
  u128: 16,
  u256: 32,
}

const MORPHO_RECORD_SIZE =
  4 * FIELD_SIZES.addr + // loanToken, collateralToken, oracle, irm
  1 * FIELD_SIZES.u128 + // lltv
  2 * FIELD_SIZES.u256 + // price, rateAtTarget
  5 * FIELD_SIZES.u128 + // totals (4) + lastUpdate
  1 * FIELD_SIZES.u128 // fee
// = 256 bytes

const MOOLAH_RECORD_SIZE =
  4 * FIELD_SIZES.addr + // loanToken, collateralToken, oracle, irm
  1 * FIELD_SIZES.u128 + // lltv
  3 * FIELD_SIZES.u256 + // loanTokenPrice, collateralTokenPrice, rateAtTarget
  5 * FIELD_SIZES.u128 + // totals (4) + lastUpdate
  1 * FIELD_SIZES.u128 // fee
// = 288 bytes

const LISTA_RECORD_SIZE =
  7 * FIELD_SIZES.addr + // loanToken, collateralToken, oracle, irm, loanProvider, collateralProvider, broker
  10 * FIELD_SIZES.u128 + // lltv, minLoan, totals (4), lastUpdate, fee, rateCap, rateFloor
  3 * FIELD_SIZES.u256 + // priceLoanToken, priceCollateralToken, rateAtTarget
  1 // hasWhitelist (bool)

/**
 * Decode packed markets bytes into typed objects.
 * Auto-detects format based on data length and decodes accordingly.
 * Integers are parsed as big-endian BigInt. Addresses are 0x-prefixed lowercase hex.
 */
export function decodeMarkets(
  input: string | Uint8Array,
): (Market | ListaMarket)[] {
  const bytes = normalizeToBytes(input)

  // Auto-detect format based on data length
  if (bytes.length % MORPHO_RECORD_SIZE === 0) {
    return decodeMorphoMarkets(bytes)
  } else if (bytes.length % LISTA_RECORD_SIZE === 0) {
    return decodeListaMarkets(bytes)
  } else {
    throw new Error(
      `Invalid data length ${bytes.length}; not a multiple of ${MORPHO_RECORD_SIZE} (morpho) or ${LISTA_RECORD_SIZE} (lista) bytes per record`,
    )
  }
}

/**
 * Decode morpho markets
 */
function decodeMorphoMarkets(bytes: Uint8Array): Market[] {
  if (bytes.length % MORPHO_RECORD_SIZE !== 0) {
    throw new Error(
      `Invalid data length ${bytes.length}; not a multiple of ${MORPHO_RECORD_SIZE} bytes per record`,
    )
  }

  const markets: Market[] = []
  for (let i = 0; i < bytes.length; i += MORPHO_RECORD_SIZE) {
    let o = i

    const loanToken = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const collateralToken = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const oracle = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const irm = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr

    const lltv = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const price = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256
    const rateAtTarget = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256

    const totalSupplyAssets = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalSupplyShares = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalBorrowAssets = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalBorrowShares = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const lastUpdate = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const fee = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128

    markets.push({
      loanToken,
      collateralToken,
      oracle,
      irm,
      lltv,
      price,
      rateAtTarget,
      totalSupplyAssets,
      totalSupplyShares,
      totalBorrowAssets,
      totalBorrowShares,
      lastUpdate,
      fee,
    })
  }

  return markets
}

/**
 * Decode moolah markets (separate loan and collateral token prices)
 */
function decodeMoolahMarkets(bytes: Uint8Array): Market[] {
  if (bytes.length % MOOLAH_RECORD_SIZE !== 0) {
    throw new Error(
      `Invalid data length ${bytes.length}; not a multiple of ${MOOLAH_RECORD_SIZE} bytes per record`,
    )
  }

  const markets: Market[] = []
  for (let i = 0; i < bytes.length; i += MOOLAH_RECORD_SIZE) {
    let o = i

    const loanToken = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const collateralToken = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const oracle = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const irm = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr

    const lltv = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const loanTokenPrice = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256
    const collateralTokenPrice = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256
    const rateAtTarget = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256

    const totalSupplyAssets = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalSupplyShares = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalBorrowAssets = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalBorrowShares = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const lastUpdate = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const fee = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128

    // follows morpho calcs: collateral price / loan price, scaled by 1e36
    // assuming moolah follows same decimal normalization as morpho blue
    const calculatedPrice =
      collateralTokenPrice && loanTokenPrice
        ? (collateralTokenPrice * 10n ** 36n) / loanTokenPrice
        : 0n

    markets.push({
      loanToken,
      collateralToken,
      oracle,
      irm,
      lltv,
      price: calculatedPrice,
      loanTokenPrice,
      collateralTokenPrice,
      rateAtTarget,
      totalSupplyAssets,
      totalSupplyShares,
      totalBorrowAssets,
      totalBorrowShares,
      lastUpdate,
      fee,
    })
  }

  return markets
}

export function decodeListaMarkets(bytes: Uint8Array): ListaMarket[] {
  if (bytes.length % LISTA_RECORD_SIZE !== 0) {
    throw new Error(
      `Invalid data length ${bytes.length}; not a multiple of ${LISTA_RECORD_SIZE} bytes per record`,
    )
  }

  const markets: ListaMarket[] = []

  for (let i = 0; i < bytes.length; i += LISTA_RECORD_SIZE) {
    let o = i

    const loanToken = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const collateralToken = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const oracle = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const irm = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr

    const lltv = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128

    const loanTokenPrice = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256
    const collateralTokenPrice = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256

    const minLoan = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128

    const rateAtTarget = readUintBE(bytes, o, FIELD_SIZES.u256)
    o += FIELD_SIZES.u256

    const totalSupplyAssets = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalSupplyShares = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalBorrowAssets = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const totalBorrowShares = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128

    // ✅ these come BEFORE hasWhitelist
    const lastUpdate = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const fee = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128

    const rateCap = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128
    const rateFloor = readUintBE(bytes, o, FIELD_SIZES.u128)
    o += FIELD_SIZES.u128

    const hasWhitelist = bytes[o] === 1
    o += 1

    const loanProvider = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const collateralProvider = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr
    const broker = readAddress(bytes, o, FIELD_SIZES.addr)
    o += FIELD_SIZES.addr

    const price =
      loanTokenPrice > 0n
        ? (collateralTokenPrice * 10n ** 36n) / loanTokenPrice
        : 0n

    markets.push({
      loanToken,
      collateralToken,
      oracle,
      irm,
      lltv,
      price,
      loanTokenPrice,
      collateralTokenPrice,
      minLoan,
      rateCap,
      rateFloor,
      hasWhitelist,
      loanProvider,
      collateralProvider,
      broker,
      rateAtTarget,
      totalSupplyAssets,
      totalSupplyShares,
      totalBorrowAssets,
      totalBorrowShares,
      lastUpdate,
      fee,
    })
  }

  return markets
}

// ---------- helpers ----------

export function normalizeToBytes(input: string | Uint8Array): Uint8Array {
  if (typeof input !== 'string') {
    return input
  }
  let hex = input.startsWith('0x') ? input.slice(2) : input
  if (hex.length % 2 !== 0) throw new Error('Hex string must have even length')
  const out = new Uint8Array(hex.length / 2)
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  }
  return out
}

function readAddress(bytes: Uint8Array, offset: number, len: number): string {
  // Addresses are the raw 20 bytes, render as 0x + 40 hex chars (lowercase).
  const hex = toHex(bytes.subarray(offset, offset + len))
  return '0x' + hex
}

function readUintBE(bytes: Uint8Array, offset: number, len: number): bigint {
  // Big-endian: (((b0 * 256) + b1) * 256 + ...) pattern.
  let v = 0n
  const end = offset + len
  for (let i = offset; i < end; i++) {
    v = (v << 8n) | BigInt(bytes[i])
  }
  return v
}

function toHex(arr: Uint8Array): string {
  let s = ''
  for (let i = 0; i < arr.length; i++) {
    const h = arr[i].toString(16).padStart(2, '0')
    s += h
  }
  return s
}
